import { useAuth0 } from "@auth0/auth0-react";
import { MoonPayProvider } from "@moonpay/moonpay-react";
import useProfile from "hooks/useProfile";
import { useRemoveActiveItem } from "hooks/useRemoveActiveItem";
import Cookies from "js-cookie";
import { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createAPI } from "services/api/api";
import { getBalanceRequest } from "store/thunks";
import { editUser, logInAccount, logOutAccount } from "store/user";
import { setupComprehensiveTokenValidation } from "utils/isTokenExpired";

const PageMoonPayWrapper = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dataFetched, setDataFetched] = useState(false);
  const [requestsInProgress, setRequestsInProgress] = useState(false);
  const {isLogged} = useProfile();
  const removeActiveItem = useRemoveActiveItem();
  const { isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();


  const fetchUserData = async (token) => {
    if (requestsInProgress) return;
    setRequestsInProgress(true);
    dispatch(editUser({field: 'isAuthLoading',value: true}))

    try {
      const sessionStorageUser = sessionStorage.getItem("user");
      if (sessionStorageUser) {
        dispatch(logInAccount(JSON.parse(sessionStorageUser)));
        setDataFetched(true);
        return;
      }

      const authorizedApi = createAPI();
      authorizedApi.interceptors.request.use((config) => {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      });

      const [balanceResponse, userResponse] = await Promise.all([
        authorizedApi.get(`/me/get_balance`),
        authorizedApi.get("/me/get_profile"),
      ]);

      const balanceData = balanceResponse.data;
      const userData = userResponse.data;

      const { name, email, nickname, auth_id, flags, phone_number } = userData;
      const {
        sound,
        expiredevent,
        newuser,
        demo,
        dashboarddemo,
        indentitycraftingdemo,
        predictioncraftingdemo,
        buypacksdemo,
        marketplacedemo,
      } = flags;

      const getProfilesData = {
        isLogged: true,
        name: name ?? "",
        email: email ?? "",
        nickname: nickname ?? "",
        phone_number: phone_number ?? "",
        auth_id: auth_id ?? "",
        flags: {
          sound: sound ?? true,
          view_expired_event: expiredevent ?? false,
          new_user: newuser ?? true,
          has_demo: demo ?? true,
          has_dashboard_demo: dashboarddemo ?? true,
          has_identity_crafting_demo: indentitycraftingdemo ?? true,
          has_prediction_crafting_demo: predictioncraftingdemo ?? true,
          has_buypacks_demo: buypacksdemo ?? true,
          has_marketplace_demo: marketplacedemo ?? true,
        },
        balance: balanceData.balance.toFixed(2) || 0,
        user_wallet: balanceData.user_wallet || null,
        token,
      };

      dispatch(logInAccount(getProfilesData));
      sessionStorage.setItem("user", JSON.stringify(getProfilesData));
    } catch (error) {
      console.error("Error fetching user data:", error);
      dispatch(logOutAccount());
    } finally {
      setRequestsInProgress(false);
      setDataFetched(true);
    }
  };

  useEffect(() => {
    const fetchDataAndSetupTokenValidation = async () => {
      if (dataFetched || isLoading) return;

      let token;
      const cookieToken = Cookies.get("token");

      if (cookieToken) {
        token = cookieToken;
      } else {
        try {
          token = await getAccessTokenSilently();
        } catch (error) {
          console.error("Error getting access token:", error);
          Cookies.remove("token");
          return;
        }
      }

      if (pathname === "/") {
        navigate("/dashboard");
      }

      await fetchUserData(token);

      const cleanupTokenValidation = setupComprehensiveTokenValidation(
        token,
        dispatch,
        loginWithRedirect
      );

      return () => {
        cleanupTokenValidation();
      };
    };

    fetchDataAndSetupTokenValidation();
  }, [dataFetched, isLoading, getAccessTokenSilently, pathname]);

  useLayoutEffect(() => {
    if (pathname === "/") {
      document.documentElement.classList.add("app--homepage");
    } else {
      document.documentElement.classList.remove("app--homepage");
    }

    dispatch(getBalanceRequest());
    removeActiveItem();
    window.scrollTo(0, 0);
  }, [pathname]);

  if (!isLogged) {
    return <>{children}</>
  }

  return <MoonPayProvider apiKey="pk_test_123">{children}</MoonPayProvider>;
};

export default PageMoonPayWrapper;
